// src/components/padrekko/qc/CaseBanner.jsx
//
// The case's identity, stage and stage-transition actions, laid out as a bar.
// Mounted in exactly one place: QcCaseBar, above the map (MapPage.jsx). It
// used to sit at the top of QcInspector's and QcSummary's panels; it moved out
// because it is case-scoped and the panel is for pad work. Renders null when no
// case is loaded.
//
// Self-contained: subscribes to useQcStore directly. EVERY GATE BELOW IS
// UNCHANGED BY THE MOVE — only the markup and the slot props are new. The bar
// (QcCaseBar) supplies what is not the banner's business through slots:
//   identityExtra  beside the case name (e.g. the customer)
//   middle         between the role and the actions (deadline, progress, counts)
//   trailing       after the actions (the exit control)
//   hideRole       narrow widths; the stage badge's title still carries the role
// The stage badge and every stage-transition action are never hidden by the
// bar's width tiers.
//
// Shows: case name, stage badge, "You: <role>" chip, read-only chip when
// caller can't write at the current stage, and the stage-transition actions.
//
// Phase 2 — customer-as-closer. customer_review has two modes, keyed on
// case.operator_pass_count:
//   Mode A (pass_count === 0, initial forward): customer forwards to the
//     operator (existing handoff) or sends back to DH (existing reverse).
//   Mode B (pass_count  >  0, final review): customer CLOSES the case (new
//     /close endpoint) or SENDS BACK to the operator for more work (new
//     /send-back endpoint, no terminal gate). The generic →DH reverse is
//     suppressed here so the action set is exactly {close, send-back-to-op}.

import { useState } from "react";
import { Loader2, GitBranch, Users, RotateCcw, Unlock, CheckCircle2, Share2, PackageCheck, AlertTriangle, ShieldOff } from "lucide-react";
import {
  useQcStore,
  nextStageFor,
  previousStageFor,
  writerRoleFor,
  selectIsCustomerInitialForward,
  selectUnresolvedKickbackCount,
} from "../../../stores/useQcStore";
import { roleLabel } from "../../../../../admin-api/lib/padrekko-qc/roleLabels.js";
import { toast } from "../../../stores/useToastStore";
import { useEffectiveOrg } from "../../../hooks/useEffectiveOrg";
import ParticipantsModal from "./ParticipantsModal";
import HandoffWarningsModal from "./HandoffWarningsModal";
import SendBackModal from "./SendBackModal";
import ReopenModal from "./ReopenModal";
import CloseCaseModal from "./CloseCaseModal";
import CustomerSendBackModal from "./CustomerSendBackModal";
import ReleaseCaseModal from "./ReleaseCaseModal";
import ShareCaseModal from "./ShareCaseModal";
import RevokeShareModal from "./RevokeShareModal";
import { RESPONSE_DEADLINE_DAYS } from "../../../../../admin-api/lib/padrekko-qc/caseStages.js";

// Stage tone palette — mirrors QcPicker's stage badges.
const STAGE_TONES = {
  dh_review:       "bg-teal-500/15    border-teal-500/40    text-teal-200",
  customer_review: "bg-amber-500/15   border-amber-500/40   text-amber-200",
  operator_review: "bg-purple-500/15  border-purple-500/40  text-purple-200",
  complete:        "bg-emerald-500/15 border-emerald-500/40 text-emerald-200",
};
const STAGE_LABELS = {
  dh_review:       "DH Review",
  customer_review: "Customer Review",
  operator_review: "Operator Review",
  complete:        "Complete",
};
// The label for the next role to receive the case at handoff, DERIVED rather
// than tabulated: the recipient of a stage IS that stage's writer, so the only
// facts here are STAGE_WRITER (via writerRoleFor) and CASE_ROLE_LABEL. The table
// this replaced carried a third stage key, `complete: "Complete"`, which nothing
// could reach — the button branches on nextStage === "complete" before the
// lookup — and which named a stage in a map whose other two values named roles.
//
// writerRoleFor, NOT writerRoleForCase: in a same-tenant case the operator_review
// writer is the owner themselves, so the case-aware form would render "Hand off
// to Case Owner" on a handoff the owner makes to themselves. Whether that is an
// improvement is a wording question, not a rename, and Phase 34 is display-only.
const nextRoleLabel = (stage) => roleLabel(writerRoleFor(stage)) || null;

// REM-576 — a refused act, in one line for a toast: the server's error, then
// its detail. Never blank: the store fills `error`, and a caller that somehow
// has neither still says which act failed.
function failureLine(result, action) {
  const error = result?.error
    || (result?.status ? `${action} failed (HTTP ${result.status})` : `${action} failed`);
  return result?.detail ? `${error} — ${result.detail}` : error;
}

// `days_30` → `30-day`, from the server's own table; an unknown basis as-is.
function basisLabel(basis) {
  const n = RESPONSE_DEADLINE_DAYS[basis];
  return n ? `${n}-day` : (basis || null);
}

export default function CaseBanner({ identityExtra = null, middle = null, trailing = null, hideRole = false } = {}) {
  const caseInfo             = useQcStore((s) => s.caseInfo);
  const caseRole             = useQcStore((s) => s.caseRole);
  const caseWriteable        = useQcStore((s) => s.caseWriteable);
  const session              = useQcStore((s) => s.session);
  const advanceStageInflight = useQcStore((s) => s.advanceStageInflight);
  const advanceStage         = useQcStore((s) => s.advanceStage);
  const loadCase             = useQcStore((s) => s.loadCase);
  const saveNextObservation  = useQcStore((s) => s.saveNextObservation);
  const nextObsSaveInflight  = useQcStore((s) => s.nextObsSaveInflight);

  const currentStage = caseInfo?.current_stage ?? null;
  const nextStage = nextStageFor(currentStage);
  const previousStage = previousStageFor(currentStage);
  const pads = session?.pads ?? [];
  const allPadsTerminal =
    pads.length > 0 && pads.every((p) => p.status === "completed" || p.status === "skipped");

  // Phase 2 — the two customer_review modes, keyed on operator handback count.
  // Mode A (pass 0) comes from the shared store selector so QcInspector and
  // this banner can't disagree about what "initial forward" means.
  const passCount = caseInfo?.operator_pass_count ?? 0;
  const isCustomer = caseRole === "customer_analyst";
  const atCustomerReview = currentStage === "customer_review";
  const customerInitialForward = useQcStore(selectIsCustomerInitialForward);       // Mode A
  const customerFinalReview    = isCustomer && atCustomerReview && passCount > 0;    // Mode B

  // ── REM-576: RELEASE AND SHARE, GUIDED RATHER THAN BLOCKED (decision 2) ──
  //
  // The server lets a stage move happen without the release or share that
  // makes the case visible to the next party (D5), so the bar puts them in
  // the right order instead: the producer's hand-off at dh_review releases
  // first; the owner shares before forwarding; and a case that has stalled
  // anyway (moved by the API, or by a reversal) gets a banner naming the
  // missing act, with the act itself for whoever may do it.
  //
  // PER ROLE, NOT PER `writeable`. The server checks the role for release,
  // share and revoke (requireCaseAccess with enforceWriteable: false): the
  // owner revokes at operator_review, where they are not the writer, and that
  // is decision 3's whole case. The operator is offered none of these.
  const participants = caseInfo?.participants ?? [];
  const ownerName = participants.find((p) => p.role === "customer_analyst")?.org_name || "the owner";
  const respondent = participants.find((p) => p.role === "operator_analyst") ?? null;
  const operatorName = respondent?.org_name || "the operator";
  const isProducer = caseRole === "dh_analyst";
  const released = !!caseInfo?.released_at;
  const shared = !!caseInfo?.shared_at;
  const open = !!currentStage && currentStage !== "complete";
  // The producer's hand-off releases first while unreleased.
  const releaseWithHandoff = isProducer && currentStage === "dh_review" && !released;
  // The owner shares before the case goes to the operator. A same-tenant case
  // has no respondent and nothing to share.
  const shareFirst = isCustomer && atCustomerReview && released && !shared && !!respondent;
  const showShareStatus = (isProducer || isCustomer) && shared && !!respondent;
  const canRevoke = isCustomer && shared && open;
  const releaseStall = isProducer && currentStage === "customer_review" && !released;
  const shareStall = (isProducer || isCustomer) && currentStage === "operator_review" && !shared && !!respondent;

  // Forward handoff. In Mode B the customer doesn't use the generic forward —
  // they Close or Send-back via dedicated endpoints — so it's hidden there.
  const canHandoff =
    !!caseInfo &&
    caseWriteable &&
    nextStage !== null &&
    allPadsTerminal &&
    !advanceStageInflight;
  const showHandoff = canHandoff && !customerFinalReview;

  // Phase 2 — block forwarding to the operator while any finding is still
  // kicked back to DH (backend enforces the same on customer_review ->
  // operator_review). Scoped to that transition via nextStage.
  const unresolvedKickbacks = useQcStore(selectUnresolvedKickbackCount);
  const forwardBlockedByKickbacks =
    nextStage === "operator_review" && unresolvedKickbacks > 0;

  const handoffButtonLabel =
    customerInitialForward
      ? "Forward to operator"
      : nextStage === "complete"
        ? "Mark complete"
        : `Hand off to ${nextRoleLabel(nextStage) ?? "next"}`;

  // Generic reverse-stage (→ previous stage; for the customer that's DH).
  // Auth: caller is current stage's writer OR a service provider. Suppressed
  // for the customer in Mode B (their send-back targets the operator instead).
  const reverseStageInflight = useQcStore((s) => s.reverseStageInflight);
  const isCurrentWriter = !!writerRoleFor(currentStage) && caseRole === writerRoleFor(currentStage);
  const isServiceProvider = useEffectiveOrg()?.is_service_provider === true;
  const canReverseStage =
    !!caseInfo &&
    !!previousStage &&
    (isCurrentWriter || isServiceProvider) &&
    !reverseStageInflight;
  const showReverseStage = canReverseStage && !customerFinalReview;

  // Phase 2 — customer-only Mode B actions.
  const closeInflight = useQcStore((s) => s.closeInflight);
  const sendBackToOperatorInflight = useQcStore((s) => s.sendBackToOperatorInflight);
  const canClose = customerFinalReview && !closeInflight;
  // Unshared, the operator could not open what it was sent back: share first.
  const canSendBackToOperator = customerFinalReview && !sendBackToOperatorInflight && !shareFirst;

  // Phase B reopen. DH service-provider only, and only at 'complete'.
  const reopenInflight = useQcStore((s) => s.reopenInflight);
  const canReopen =
    !!caseInfo &&
    currentStage === "complete" &&
    isServiceProvider &&
    !reopenInflight;

  // Lightweight stage hint for the customer, distinguishing the two modes.
  const customerModeHint = customerInitialForward
    ? (shareFirst
        ? `Customer review — verify findings, share with ${operatorName}, then forward to operator`
        : "Customer review — verify findings, then forward to operator")
    : customerFinalReview
      ? "Operator QC complete — review findings, then close or send back"
      : null;

  // Post-close: surface the close reason (if any) recorded in stage history.
  const closeEntry =
    currentStage === "complete"
      ? [...(caseInfo?.stage_history ?? [])].reverse().find((h) => h?.direction === "close")
      : null;

  // The soft disposition gate. The first attempt carries no acknowledgment, so
  // the server refuses once with the count; the acknowledged retry always
  // proceeds. When everything is dispositioned there is no 409 and no modal —
  // the gate is invisible in the normal case, which is the only way it stays
  // meaningful in the abnormal one.
  const [handoffWarnings, setHandoffWarnings] = useState(null);

  async function handleHandoff(acknowledge = false) {
    if (!showHandoff) return;
    const result = await advanceStage({
      toStage: nextStage,
      notes: null,
      acknowledgeHandoff: acknowledge,
    });
    if (result.ok) {
      setHandoffWarnings(null);
      if (result.warning) {
        toast.warning(`Warning: ${result.warning.detail}`);
      }
    } else if (result.handoffWarnings) {
      setHandoffWarnings(result.handoffWarnings);
    } else if (result.conflict) {
      setHandoffWarnings(null);
      toast.error(
        `Stage advanced under you (now '${result.conflict.current_stage}'). Refreshing case…`
      );
      await loadCase(caseInfo.id);
    } else {
      // Any other refusal, in the server's words. The store's `error` is set
      // too, but only the side panel shows that, and a hand-off that fails
      // without a word here reads as a button that did nothing (REM-576).
      setHandoffWarnings(null);
      toast.error(failureLine(result, "Hand-off"));
    }
  }

  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [sendBackOpen, setSendBackOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [custSendBackOpen, setCustSendBackOpen] = useState(false);
  // REM-576. releaseOpen: null | "handoff" | "release".
  const [releaseOpen, setReleaseOpen] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);

  // Next-observation edit (UL only). Draft seeds from the current value.
  const [nextObsEditing, setNextObsEditing] = useState(false);
  const [nextObsDraft, setNextObsDraft] = useState("");

  async function commitNextObs() {
    if (!nextObsDraft || !caseInfo) return;
    const result = await saveNextObservation(caseInfo.id, nextObsDraft);
    if (result.ok) {
      setNextObsEditing(false);
    } else {
      toast.error(`Could not update date: ${result.error ?? "unknown error"}`);
    }
  }

  if (!caseInfo) return null;

  // Honest basis copy — derived recommendation, never an autonomous schedule.
  const nextObsBasisLabel =
    caseInfo.next_observation_basis === "deadline"
      ? "based on outstanding remediation deadlines"
      : caseInfo.next_observation_basis === "routine"
        ? "routine re-observation cadence"
        : null;

  const roleText = roleLabel(caseRole) || "—";
  const hasExtras =
    !!customerModeHint ||
    currentStage === "complete" ||
    showShareStatus || releaseStall || shareStall;

  return (
    <>
    <div className="border-b border-slate-800 bg-slate-900" data-qc-case-banner>
      <div className="flex items-center gap-3 px-3 py-1.5 min-w-0">
        <div className="min-w-0 flex-1 flex items-baseline gap-2">
          {/* No visible "CASE" label: the bar's position and the bold name
              set the context. Screen readers get it in words, since they do
              not see the layout. */}
          <span className="sr-only">Case:</span>
          <span className="text-[14px] text-slate-100 font-medium truncate min-w-[60px]" title={caseInfo.name}>
            {caseInfo.name}
          </span>
          {/* Project tie — resolved server-side inside the access-gated case
              read. Surfaces uniformly for every persona (the operator has no
              project picker, so this label is its sole project context). */}
          {caseInfo.project_name && (
            <span className="text-[12px] text-fg-secondary truncate min-w-0" title={`Project: ${caseInfo.project_name}`}>
              {caseInfo.project_name}
            </span>
          )}
          {identityExtra}
        </div>
        {!hideRole && (
          <div className="text-[12px] text-fg-secondary whitespace-nowrap shrink-0" data-qc-role>
            {/* The SIXTH site, and the second in this file — an inline ternary
                rather than a map, which is why the recon's scan for declarations
                did not see it. It carried the full old names; it keeps the full
                new ones. QcPicker's matching "You:" row is short because its card
                is narrower, not because the two disagree about the role. */}
            You: {roleText}
            {!caseWriteable && (
              <>
                <span className="text-slate-700 mx-1">·</span>
                <span className="text-amber-400/80 italic">read-only</span>
              </>
            )}
          </div>
        )}
        {middle}
        <div className="flex items-center gap-1.5 shrink-0" data-qc-stage-actions>
          <button
            type="button"
            onClick={() => setParticipantsOpen(true)}
            className="flex items-center justify-center w-5 h-5 rounded
                       text-fg-secondary hover:text-slate-200 hover:bg-slate-800
                       transition"
            title="View participants"
            aria-label="View participants"
          >
            <Users size={11} />
          </button>
          <span
            data-qc-stage
            title={`Stage: ${STAGE_LABELS[currentStage] ?? currentStage} · You: ${roleText}${caseWriteable ? "" : " (read-only)"}`}
            className={`text-[10px] font-mono uppercase tracking-widest border rounded px-1 py-[1px]
                            ${STAGE_TONES[currentStage] ?? "bg-slate-800 border-slate-700 text-slate-400"}`}>
            {currentStage === "complete" ? "Closed" : (STAGE_LABELS[currentStage] ?? currentStage)}
          </span>
          {showReverseStage && (
            <button
              type="button"
              onClick={() => setSendBackOpen(true)}
              disabled={reverseStageInflight}
              className="flex items-center gap-1 text-[12px] px-2 py-1 rounded border
                         bg-amber-500/10 border-amber-500/40 text-amber-200
                         hover:bg-amber-500/20
                         disabled:opacity-50 disabled:cursor-not-allowed transition"
              title={`Send the case back to ${STAGE_LABELS[previousStage] ?? previousStage}`}
            >
              {reverseStageInflight
                ? <Loader2 size={10} className="animate-spin" />
                : <RotateCcw size={10} />}
              Send back
            </button>
          )}
          {canSendBackToOperator && (
            <button
              type="button"
              onClick={() => setCustSendBackOpen(true)}
              disabled={sendBackToOperatorInflight}
              className="flex items-center gap-1 text-[12px] px-2 py-1 rounded border
                         bg-amber-500/10 border-amber-500/40 text-amber-200
                         hover:bg-amber-500/20
                         disabled:opacity-50 disabled:cursor-not-allowed transition"
              title="Send the case back to the operator for more work"
            >
              {sendBackToOperatorInflight
                ? <Loader2 size={10} className="animate-spin" />
                : <RotateCcw size={10} />}
              Send back to operator
            </button>
          )}
          {canReopen && (
            <button
              type="button"
              onClick={() => setReopenOpen(true)}
              disabled={reopenInflight}
              className="flex items-center gap-1 text-[12px] px-2 py-1 rounded border
                         bg-emerald-500/10 border-emerald-500/40 text-emerald-200
                         hover:bg-emerald-500/20
                         disabled:opacity-50 disabled:cursor-not-allowed transition"
              title="Reopen this completed case"
            >
              {reopenInflight
                ? <Loader2 size={10} className="animate-spin" />
                : <Unlock size={10} />}
              Reopen
            </button>
          )}
          {canClose && (
            <button
              type="button"
              onClick={() => setCloseOpen(true)}
              disabled={closeInflight}
              className="flex items-center gap-1 text-[12px] px-2 py-1 rounded border
                         bg-emerald-500/15 border-emerald-500/50 text-emerald-200
                         hover:bg-emerald-500/25
                         disabled:opacity-50 disabled:cursor-not-allowed transition"
              title="Close the case — marks it complete and read-only"
            >
              {closeInflight
                ? <Loader2 size={10} className="animate-spin" />
                : <CheckCircle2 size={10} />}
              Close case
            </button>
          )}
          {shareFirst && (
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              className="flex items-center gap-1 text-[12px] px-2 py-1 rounded border
                         bg-emerald-500/15 border-emerald-500/50 text-emerald-200
                         hover:bg-emerald-500/25 transition"
              title={`Share the case with ${operatorName} and set its response deadline`}
            >
              <Share2 size={10} />
              {`Share with ${operatorName}…`}
            </button>
          )}
          {showHandoff && releaseWithHandoff && (
            <button
              type="button"
              onClick={() => setReleaseOpen("handoff")}
              disabled={advanceStageInflight}
              className="flex items-center gap-1 text-[12px] px-2 py-1 rounded border
                         bg-[#16f2d4]/15 border-[#16f2d4]/50 text-[#16f2d4]
                         hover:bg-[#16f2d4]/25
                         disabled:opacity-50 disabled:cursor-not-allowed transition"
              title={`Release the case to ${ownerName}, then advance it to ${nextStage}`}
            >
              <PackageCheck size={10} />
              {`Release to ${ownerName} and hand off`}
            </button>
          )}
          {showHandoff && !releaseWithHandoff && !shareFirst && (
            <button
              type="button"
              onClick={() => handleHandoff(false)}
              disabled={advanceStageInflight || forwardBlockedByKickbacks}
              className="flex items-center gap-1 text-[12px] px-2 py-1 rounded border
                         bg-[#16f2d4]/15 border-[#16f2d4]/50 text-[#16f2d4]
                         hover:bg-[#16f2d4]/25
                         disabled:opacity-50 disabled:cursor-not-allowed transition"
              title={forwardBlockedByKickbacks
                ? "Resolve pending DH resubmissions before forwarding to operator"
                : `Advance the case to ${nextStage}`}
            >
              {advanceStageInflight
                ? <Loader2 size={10} className="animate-spin" />
                : <GitBranch size={10} />}
              {advanceStageInflight ? "Handing off…" : handoffButtonLabel}
            </button>
          )}
          {trailing}
        </div>
      </div>

      {hasExtras && (
        <div className="px-3 pb-1.5">
          {/* REM-576 stall banners: the stage says it is someone's turn and
              that party cannot see the case. Each names the missing act and
              offers it to whoever may do it. */}
          {releaseStall && (
            <div className="mt-1 flex items-center gap-2 text-[11px] text-amber-200 leading-snug" data-qc-stall="release">
              <AlertTriangle size={11} className="shrink-0 text-amber-400" />
              <span>Not released — {ownerName} can't see this case</span>
              <button
                type="button"
                onClick={() => setReleaseOpen("release")}
                className="px-1.5 py-[1px] rounded border border-sky-500/40 bg-sky-500/10 text-sky-200
                           hover:bg-sky-500/20 transition"
              >
                Release
              </button>
            </div>
          )}
          {shareStall && (
            <div className="mt-1 flex items-center gap-2 text-[11px] text-amber-200 leading-snug" data-qc-stall="share">
              <AlertTriangle size={11} className="shrink-0 text-amber-400" />
              <span>Not shared — {operatorName} can't see this case</span>
              {isCustomer && (
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="px-1.5 py-[1px] rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-200
                             hover:bg-emerald-500/20 transition"
                >
                  Share…
                </button>
              )}
            </div>
          )}
          {/* Who holds the case as respondent, and the term they are held to.
              The producer and the owner see it; the operator has its own
              "Due" in the bar. */}
          {showShareStatus && (
            <div className="mt-1 flex items-center gap-2 text-[11px] leading-snug">
              <span className="text-emerald-300/90" data-qc-share-status>
                Shared with {operatorName}
                {caseInfo.response_deadline_date
                  ? ` · due ${caseInfo.response_deadline_date}${basisLabel(caseInfo.response_deadline_basis) ? ` (${basisLabel(caseInfo.response_deadline_basis)})` : ""}`
                  : ""}
              </span>
              {canRevoke && (
                <button
                  type="button"
                  onClick={() => setRevokeOpen(true)}
                  className="flex items-center gap-1 px-1.5 py-[1px] rounded border border-amber-500/40
                             bg-amber-500/10 text-amber-200 hover:bg-amber-500/20 transition"
                >
                  <ShieldOff size={10} />
                  Revoke share…
                </button>
              )}
            </div>
          )}
          {/* Customer mode hint — lightweight subtitle distinguishing the
              initial-forward vs final-review states. */}
          {customerModeHint && (
            <div className="mt-1 text-[11px] text-fg-secondary italic leading-snug">
              {customerModeHint}
            </div>
          )}

          {/* Post-close note — surfaces the closure + optional reason. */}
          {currentStage === "complete" && (
            <div className="mt-1 text-[11px] text-emerald-300/80 leading-snug">
              Closed{closeEntry?.notes ? <span className="text-slate-400"> · {closeEntry.notes}</span> : null}
            </div>
          )}

          {/* Recommended next observation — derived from committed remediation
              deadlines (or a routine cadence). UL may adjust the date. This is a
              recommendation, NOT an autonomous schedule. */}
          {currentStage === "complete" && caseInfo.next_observation_date && (
            <div className="mt-1 pt-1 border-t border-slate-800/60 text-[11px] leading-snug">
              <div className="font-mono uppercase tracking-widest text-fg-secondary">
                Recommended next observation
              </div>
              {nextObsEditing ? (
                <div className="mt-0.5 flex items-center gap-1.5 flex-wrap">
                  <input
                    type="date"
                    value={nextObsDraft}
                    onChange={(e) => setNextObsDraft(e.target.value)}
                    className="bg-slate-800 border border-slate-700 rounded px-1 py-[1px]
                               text-[12px] text-slate-100"
                  />
                  <button
                    type="button"
                    onClick={commitNextObs}
                    disabled={nextObsSaveInflight || !nextObsDraft}
                    className="px-1.5 py-[1px] rounded border border-sky-500/40
                               bg-sky-500/10 text-sky-200 hover:bg-sky-500/20
                               disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    {nextObsSaveInflight ? "Saving…" : "Save"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setNextObsEditing(false)}
                    disabled={nextObsSaveInflight}
                    className="px-1.5 py-[1px] rounded border border-slate-700
                               text-slate-400 hover:text-slate-200 transition"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="mt-0.5 flex items-center gap-1.5 flex-wrap">
                  <span className="text-sky-200 font-medium">
                    {caseInfo.next_observation_date}
                  </span>
                  {nextObsBasisLabel && (
                    <span className="text-fg-secondary">· {nextObsBasisLabel}</span>
                  )}
                  {isCustomer && (
                    <button
                      type="button"
                      onClick={() => {
                        setNextObsDraft(caseInfo.next_observation_date);
                        setNextObsEditing(true);
                      }}
                      className="text-sky-400/70 hover:text-sky-300 underline underline-offset-2"
                    >
                      Edit
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
    <ParticipantsModal
      isOpen={participantsOpen}
      onClose={() => setParticipantsOpen(false)}
    />
    <HandoffWarningsModal
      isOpen={handoffWarnings !== null}
      detail={handoffWarnings}
      action="forward"
      busy={advanceStageInflight}
      onConfirm={() => handleHandoff(true)}
      onClose={() => setHandoffWarnings(null)}
    />
    <SendBackModal
      isOpen={sendBackOpen}
      onClose={() => setSendBackOpen(false)}
    />
    <ReopenModal
      isOpen={reopenOpen}
      onClose={() => setReopenOpen(false)}
    />
    <CloseCaseModal
      isOpen={closeOpen}
      onClose={() => setCloseOpen(false)}
    />
    <CustomerSendBackModal
      isOpen={custSendBackOpen}
      onClose={() => setCustSendBackOpen(false)}
    />
    <ReleaseCaseModal
      isOpen={releaseOpen !== null}
      onClose={() => setReleaseOpen(null)}
      ownerName={ownerName}
      handOff={releaseOpen === "handoff"}
      nextStage={nextStage}
      nextStageLabel={STAGE_LABELS[nextStage] ?? null}
    />
    <ShareCaseModal
      isOpen={shareOpen}
      onClose={() => setShareOpen(false)}
      operatorName={operatorName}
    />
    <RevokeShareModal
      isOpen={revokeOpen}
      onClose={() => setRevokeOpen(false)}
      operatorName={operatorName}
      movesBack={currentStage === "operator_review"}
    />
    </>
  );
}
